import type { ApolloError, QueryResult } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import { useParams, useSearch } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { DEFAULT_SIZE, LIBRARY_COLLECTION_NAME, SYSTEM } from 'src/constants';
import { allCollections } from 'src/dynamoQueries/collections';
import { openSearchToAllItems } from 'src/dynamoQueries/queryHelpers';
import { buildSimpleOSQuery } from 'src/dynamoQueries/search';
import type { OpenSearchData as DynamoOpenSearchData } from 'src/dynamoQueries/types';
import type {
  CollectionItem,
  GalleryRouteParams,
  MediaItem,
} from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/types';
import { cleanPayload } from 'src/helpers/common';
import { scopeToItemType } from 'src/helpers/scope';
import { processSearchParams } from 'src/helpers/searchHelper';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import viewStateVar from 'src/store/viewState';
import type { AssetItem } from 'src/types/store.types';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

interface CollectionQueryVariables {
  id?: string;
  name?: string;
  collectionType?: string;
}

interface CollectionData {
  items?: {
    totalCount: number;
  };
}

interface OpenSearchQueryVariables {
  q: string;
  itemType?: string;
}

interface OpenSearchResult {
  contentObject: MediaItem | CollectionItem;
}

interface OpenSearchQueryData {
  opensearch?: {
    totalCount: number;
    results?: OpenSearchResult[];
  };
}

interface LibrarySearchResultsContextValue {
  items: (MediaItem | CollectionItem)[];
  assetItems: AssetItem[];
  searchableCount: number | null;
  trueTotalCount: number | null;
  loading: boolean;
  initialLoading: boolean;
  error?: ApolloError;
  refetch: QueryResult<OpenSearchQueryData, OpenSearchQueryVariables>['refetch'];
}

const collectionCountQuery = gql(jsonToGraphQLQuery(allCollections(true), { pretty: true }));

const LibrarySearchResultsContext = createContext<LibrarySearchResultsContextValue | null>(null);

const getCollectionVariables = (galleryId?: string): CollectionQueryVariables => {
  if (galleryId) return { id: galleryId };
  return { name: LIBRARY_COLLECTION_NAME, collectionType: SYSTEM };
};

const getItemsFromOpenSearch = (opensearchResults?: OpenSearchQueryData): (MediaItem | CollectionItem)[] => {
  if (!opensearchResults?.opensearch) return [];

  const osResults = openSearchToAllItems(opensearchResults as unknown as DynamoOpenSearchData, [
    'trExpressions',
    'highlights',
    'highlightLabels',
  ]);
  const cleanedData = cleanPayload<OpenSearchResult>(osResults);
  return cleanedData.map((result) => result.contentObject);
};

const getAssetItems = (items: (MediaItem | CollectionItem)[]): AssetItem[] =>
  items.filter((item) => !item.__typename?.includes('Collection')) as unknown as AssetItem[];

export const LibrarySearchResultsProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const params = useParams({ strict: false }) as GalleryRouteParams;
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const galleryId = params.galleryId;

  const collectionVariables = useMemo(() => getCollectionVariables(galleryId), [galleryId]);
  const { data: collections, loading: collectionLoading } = useQuery(collectionCountQuery, {
    variables: collectionVariables,
    fetchPolicy: 'cache-and-network',
  });

  const queryVariables = useMemo((): OpenSearchQueryVariables => {
    const sanitized = processSearchParams(search, galleryId).replace(/%3A/g, ':');
    const q = sanitized && sanitized.length > 0 ? sanitized : `size=${DEFAULT_SIZE}&start=0`;
    const itemType = scopeToItemType(search.scope);
    return { q, itemType };
  }, [search, galleryId]);

  const {
    data: opensearchResults,
    refetch,
    loading,
    error,
  } = useQuery<OpenSearchQueryData, OpenSearchQueryVariables>(buildSimpleOSQuery(), {
    fetchPolicy: 'cache-and-network',
    skip: false,
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  });

  const lastOpenSearchResultsRef = useRef<OpenSearchQueryData | undefined>(undefined);

  useEffect(() => {
    if (opensearchResults?.opensearch) {
      lastOpenSearchResultsRef.current = opensearchResults;
    }
  }, [opensearchResults]);

  const visibleOpenSearchResults = opensearchResults?.opensearch
    ? opensearchResults
    : loading
      ? lastOpenSearchResultsRef.current
      : opensearchResults;

  const items = useMemo(() => getItemsFromOpenSearch(visibleOpenSearchResults), [visibleOpenSearchResults]);
  const assetItems = useMemo(() => getAssetItems(items), [items]);

  const searchableCount = visibleOpenSearchResults?.opensearch
    ? visibleOpenSearchResults.opensearch.totalCount
    : loading
      ? null
      : 0;

  const collection = cleanPayload<CollectionData>(collections)[0];
  const trueTotalCount =
    typeof collection?.items?.totalCount === 'number' ? collection.items.totalCount : collectionLoading ? null : 0;

  const initialLoading = !visibleOpenSearchResults?.opensearch && loading;

  useEffect(() => {
    if (trueTotalCount === null) return;
    viewStateVar({
      ...viewStateVar(),
      pagination: {
        ...viewStateVar().pagination,
        totalCount: trueTotalCount,
      },
    });
  }, [trueTotalCount]);

  useEffect(() => {
    akomiMediaCardGridVar({
      ...akomiMediaCardGridVar(),
      assetItems,
    });
  }, [assetItems]);

  const value = useMemo(
    () => ({
      items,
      assetItems,
      searchableCount,
      trueTotalCount,
      loading,
      initialLoading,
      error,
      refetch,
    }),
    [items, assetItems, searchableCount, trueTotalCount, loading, initialLoading, error, refetch],
  );

  return <LibrarySearchResultsContext.Provider value={value}>{children}</LibrarySearchResultsContext.Provider>;
};

export const useLibrarySearchResults = (): LibrarySearchResultsContextValue => {
  const context = useContext(LibrarySearchResultsContext);
  if (!context) {
    throw new Error('useLibrarySearchResults must be used within LibrarySearchResultsProvider');
  }
  return context;
};

export const useMaybeLibrarySearchResults = (): LibrarySearchResultsContextValue | null =>
  useContext(LibrarySearchResultsContext);
